import React from 'react';
import { cn } from '../../utils/cn';
import { Image } from '../../atoms/image';
import { Text } from '../../atoms/text';
import { Icon } from '../../atoms/icon';
import { Button } from '../../atoms/button';
import type { PhoneInputProps } from './phone-input.types';
import { getColorVar } from '../../../theme';

import styles from './phone-input.module.css';

const PhoneInput: React.FC<PhoneInputProps> = ({
    value = '',
    onChange,
    onReset,
    onBlur,
    onFocus,
    placeholder = 'Enter phone number',
    errorMessage,
    countryCode = '+84',
    flagIconProps,
    disabled = false,
    label,
    helperText,
    showRightIcon = true,
    className,
    inputClassName,
    testID = 'phone-input',
    autoFocus = false,
    maxLength = 15,
}) => {
    const hasError = Boolean(errorMessage);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        // Only allow numeric input
        const numericValue = inputValue.replace(/[^0-9]/g, '');
        onChange?.(numericValue);
    };

    return (
        <div data-testid={testID} className={cn(styles.container, className)}>
            {label && (
                <div className={styles.labelWrapper}>
                    <Text showIf={!!label} className={styles.label}>
                        {label}
                    </Text>
                    <Text className={styles.labelMark}>(*)</Text>
                </div>
            )}

            <div className={styles.formLabel}>
                <div className={styles.formLabelChip}>
                    <Image
                        {...flagIconProps}
                        showIf={!!flagIconProps?.src && !!flagIconProps?.alt}
                        src={flagIconProps?.src ?? ''}
                        alt={flagIconProps?.alt ?? ''}
                        className={cn(styles.flagImage, flagIconProps?.className)}
                    />
                    <Text className={styles.labelChip}>{countryCode}</Text>
                </div>
                <div className={styles.inputFieldWrapper}>
                    <div className={styles.inputField}>
                        {/* Phone Number Input */}
                        <input
                            type="tel"
                            inputMode="numeric"
                            value={value}
                            onChange={handleChange}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            placeholder={placeholder}
                            disabled={disabled}
                            autoFocus={autoFocus}
                            maxLength={maxLength}
                            data-testid="phone-input-field"
                            className={cn(styles.input, inputClassName)}
                            aria-invalid={hasError}
                            aria-describedby={(() => {
                                if (hasError) return `${testID}-error`;
                                if (helperText) return `${testID}-helper`;
                                return undefined;
                            })()}
                        />

                        {/* This button is inside a form, so we must set type="button" to avoid unintended form submission (default is "submit") */}
                        <Button
                            type="button"
                            variant="text"
                            showIf={!!showRightIcon}
                            disabled={disabled}
                            onClick={onReset}
                            className={styles.clearButton}
                        >
                            <Icon
                                name="circle-x"
                                color={getColorVar('slate', '80')}
                                containerClassName={styles.clearButtonIcon}
                            />
                        </Button>
                    </div>

                    <Text showIf={!!errorMessage} className={styles.errorMessage}>
                        {errorMessage}
                    </Text>

                    <Text showIf={!!helperText} className={styles.helperText}>
                        {helperText}
                    </Text>
                </div>
            </div>
        </div>
    );
};

export default PhoneInput;
